import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@activecollab/components";
import { Editor } from "@tinymce/tinymce-react";
import Prism from "prismjs";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { CodeBlockDialog, languageCodeBlockList } from "~/components/TinyMCEDialogs/CodeBlockDialog/CodeBlockDialog";
import { highlightedCode } from "~/components/TinyMCEDialogs/CodeHighLighter";
import { LinkDialog } from "~/components/TinyMCEDialogs/LinkDialog/LinkDialog";
import { TableDialog } from "~/components/TinyMCEDialogs/TableDialog/TableDialog";
import { useVisualEditorContext } from "~/components/VisualEditor/context";
import {
  buttonsRegistry as calloutButtonRegistry,
  menuOptions as calloutMenuOptions,
} from "~/custom/tinymce/plugins/a51callout/calloutButtons";
import Angie from "~/utils/angie.jsx";
import upload from "~/utils/upload.jsx";
import { useFileTokenContext } from "../FilePreview";
import { VariableDialogWrapper } from "../TinyMCEDialogs/VariableDialog/VariableDialogWrapper";

const imgAttrs = [
  `class`,
  `src`,
  `border=0`,
  `alt`,
  `title`,
  `hspace`,
  `vspace`,
  `width`,
  `height`,
  `align`,
  `onmouseover`,
  `onmouseout`,
  `name`,
  `image-type`,
  `object-id`,
];

const VisualEditor = ({
  content,
  bodyMode,
  onChange,
  directionality,
  placeholder,
  autocompleteConfig,
  onKeyDown,
  autoFocus,
  onFileUploadFailed,
  showTextVariables,
}) => {
  const { t } = useTranslation();

  const { download_token, thumb_token } = useFileTokenContext();
  const { setIsEditorFocused } = useVisualEditorContext();
  const tinymceId = useMemo(() => `tinymce_${v4()}`, []);
  const tinymceToolbarId = `${tinymceId}_toolbar`;

  const editorRef = useRef(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkData, setLinkData] = useState({ url: "", text: "", existingLinkNode: null });
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [codeBlockDialogOpen, setCodeBlockDialogOpen] = useState(false);
  const [codeBlockData, setCodeBlockData] = useState({ language: "", code: "", existingCodeBlockNode: null });
  const [deleteTableDialog, setDeleteTableDialog] = useState(false);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);

  const handleInsertLink = (url, text) => {
    const editor = editorRef.current;
    const { existingLinkNode } = linkData;

    if (editor) {
      if (existingLinkNode) {
        editor.dom.setAttrib(existingLinkNode, "href", url);
        existingLinkNode.textContent = text;
      } else {
        editor.selection.setContent(`<a href="${url}" data-mce-href="${url}">${text}</a>`);
      }
    }
  };

  const handleInsertTable = (rows, columns) => {
    const editor = editorRef.current;
    if (editor) {
      editor.execCommand("mceInsertTable", false, { rows, columns });
    }
  };

  const handleCodeBlockInsert = (language, code) => {
    const editor = editorRef.current;
    const { existingCodeBlockNode } = codeBlockData;

    if (editor) {
      const grammar = Prism.languages[language];
      let codePrism = grammar ? highlightedCode(language, code) : code;

      if (existingCodeBlockNode) {
        existingCodeBlockNode.setAttribute("data-language", language);
        existingCodeBlockNode.setAttribute("class", `language-${language}`);
        existingCodeBlockNode.setAttribute("contenteditable", "false");
        editor.dom.setHTML(existingCodeBlockNode, codePrism);
      } else {
        const content = editor.dom.createHTML(
          "pre",
          {
            contenteditable: "false",
            "data-mce-highlighted": "true",
            class: `language-${language}`,
            "data-src-status": "loaded",
            "data-mce-type": "code",
            "data-language": `${language}`,
          },
          codePrism
        );
        editor.insertContent(content);
      }
    }

    setCodeBlockDialogOpen(false);
  };

  const handleDeleteTable = () => {
    const editor = editorRef.current;
    if (editor) {
      editor.execCommand("mceTableDelete");
    }
    setDeleteTableDialog(false);
  };

  const handleInsertVariable = (text) => {
    const editor = editorRef.current;
    if (editor && text) {
      editor.selection.setContent(`<span>${text}</span>`);
    }
  };

  const openLinkDialog = () => {
    const editor = editorRef.current;
    const selectedNode = editor.selection.getNode();
    const selectedText = editor.selection.getContent({ format: "text" });
    const linkNode = editor.dom.getParent(selectedNode, "a[href]");

    const url = linkNode ? linkNode.getAttribute("href") : "";
    const text = linkNode ? linkNode.textContent : selectedText;

    setLinkData({ url, text, existingLinkNode: linkNode });
    setLinkDialogOpen(true);
  };

  const closeLinkDialog = () => {
    setLinkDialogOpen(false);
  };

  const openTableDialog = () => {
    setTableDialogOpen(true);
  };

  const closeTableDialog = () => {
    setTableDialogOpen(false);
  };

  const openCodeBlockDialog = () => {
    const editor = editorRef.current;
    const selectedNode = editor.selection.getNode();
    const selectedCode = editor.selection.getContent({ format: "text" });
    const codeBlockNode = editor.dom.getParent(selectedNode, "pre");

    const language = codeBlockNode ? codeBlockNode.getAttribute("data-language") : "";
    const code = codeBlockNode ? codeBlockNode.outerText : selectedCode;

    setCodeBlockData({ language, code, existingCodeBlockNode: codeBlockNode });
    setCodeBlockDialogOpen(true);
  };

  const plugins = useMemo(() => {
    return [
      "a51image",
      "a51autocomplete",
      "a51noneditable",
      "lists",
      "a51listpaste",
      "a51paste",
      "a51callout",
      "table",
      "codesample",
    ];
  }, []);

  const openDeleteTableDialog = () => {
    setDeleteTableDialog(true);
  };

  const closeDeleteTableDialog = () => {
    setDeleteTableDialog(false);
  };

  const openVariableDialog = () => {
    setVariableDialogOpen(true);
  };

  const closeVariableDialog = () => {
    setVariableDialogOpen(false);
  };

  const config = {
    placeholder,
    icons: "a51icons",
    skin: false,
    contextmenu: false,
    link_title: false,
    target_list: false,
    plugins: plugins,
    menubar: false,
    br_in_pre: false,
    toolbar_persist: true,
    fixed_toolbar_container: `#${tinymceToolbarId}`,
    statusbar: false,
    inline: true,
    object_resizing: false,
    extended_valid_elements: `img[${imgAttrs.join(`|`)}]`,
    relative_urls: false,
    directionality,
    browser_spellcheck: true,
    toolbar: `bold italic formatingOptionsMenu | bullist numlist | indent outdent | image link insertOptionsMenu | note blockquote outlineOptionsMenu`,
    table_toolbar:
      "confirmTableDelete tablecaption | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
    branding: false,
    promotion: false,
    table_default_attributes: {
      class: "visual_editor_table",
    },
    formats: {
      h1: { block: "h1", classes: "visual_editor_h1" },
      h2: { block: "h2", classes: "visual_editor_h2" },
      strikethrough: { inline: "span", styles: { "text-decoration": "line-through" }, exact: true },
      underline: { inline: "span", styles: { "text-decoration": "underline" }, exact: true },
      removeformat: [
        {
          selector: "b, strong, em, i, u, s, strike, del, code, h1, h2",
          remove: "all",
          split: true,
          expand: false,
          deep: true,
        },
        {
          selector: 'span[style*="text-decoration: underline"], span[style*="text-decoration-line: underline"]',
          remove: "all",
          split: true,
          expand: false,
          deep: true,
        },
        {
          selector: 'span[style*="text-decoration: line-through"], span[style*="text-decoration-line: line-through"]',
          remove: "all",
          split: true,
          expand: false,
          deep: true,
        },
      ],
    },
    table_use_colgroups: false,
    init_instance_callback: (editor) => {
      let focusedElement = document.activeElement;
      editor.focus();
      setIsEditorFocused(true);
      if (!autoFocus) {
        setTimeout(() => {
          focusedElement.focus();
          setIsEditorFocused(false);
        });
      }
      editor.on("focus", function () {
        setIsEditorFocused(true);
      });
      editor.on("blur", function () {
        setIsEditorFocused(false);
      });
      editor.on("remove", function () {
        setIsEditorFocused(false);
      });
    },
    a51noneditable_selectors: [`.mention`, `.new_mention`, `.hashtag`, `.new_hashtag`],
    a51image: {
      upload: (files, callback, error_callback) => {
        const file = files[0];
        if (file) {
          upload(file)(new XMLHttpRequest()).then(
            (response) => {
              const { thumbnail_url, code, name } = response;
              let src = thumbnail_url
                .replace(`--WIDTH--`, `500`)
                .replace(`--HEIGHT--`, `300`)
                .replace(`--SCALE--`, `scale`)
                .replace("--THUMBNAIL-TOKEN--", thumb_token)
                .replace("--DOWNLOAD-TOKEN--", download_token);

              callback({ src, code, name });
            },
            (error) => {
              error_callback();
              onFileUploadFailed(error);
            }
          );
        }
      },
      insert: (item) => {
        const { src, code, name } = item;
        return `<img src="${src}" image-type="attachment" object-id="${code}" alt="${name}" />`;
      },
      loader_src: `${Angie.assets_url}/environment/images/layout/loader.gif`,
      paste_images: true,
    },
    a51autocomplete: autocompleteConfig,
    codesample_languages: languageCodeBlockList,
    codesample_global_prismjs: true,
  };

  if (bodyMode === "paragraph") {
    config.forced_root_block = "p";
    config.text_patterns = [
      { start: "1. ", cmd: "InsertOrderedList" },
      { start: "* ", cmd: "InsertUnorderedList" },
      { start: "- ", cmd: "InsertUnorderedList" },
      { start: "---", replacement: "<hr/>" },
      { start: "**", end: "**", format: "bold" },
      { start: "*", end: "*", format: "italic" },
      { start: "_", end: "_", format: "underline" },
      { start: ">", format: "blockquote" },
      { start: "`", end: "`", format: "code" },
      { start: "#", format: "h1" },
      { start: "##", format: "h2" },
    ];
  }

  const handleEditorChange = useCallback(
    (e, editor) => {
      let content = editor.getContent();

      if (content) {
        content = content
          .replace(/^<p><br[^>]*data-mce-bogus="1"[^>]*><\/p>/i, "")
          .replace(/<p><br[^>]*data-mce-bogus="1"[^>]*><\/p>$/i, "");
      }

      onChange(content);
    },
    [onChange]
  );

  const handleInit = (event, editor) => {
    editorRef.current = editor;

    const invalidCalloutSelection = () => {
      const rng = editor.selection.getRng();
      const fragment = rng.cloneContents();

      const containAside = fragment.querySelector && fragment.querySelector("aside.callout-wrapper") !== null;

      const startAside = editor.dom.getParent(editor.selection.getStart(), "aside.callout-wrapper");
      const endAside = editor.dom.getParent(editor.selection.getEnd(), "aside.callout-wrapper");

      return endAside !== startAside || (!startAside && !endAside && containAside);
    };

    editor.on(
      "dblclick",
      (e) => {
        const node = e.target;

        const isCodeSample = node && ((node.closest && node.closest("pre")) || node.nodeName === "PRE");

        if (isCodeSample) {
          e.preventDefault();
          e.stopImmediatePropagation();
          openCodeBlockDialog();
          return false;
        }
      },
      true
    );

    editor.ui.registry.addToggleButton("bold", {
      icon: "bold",
      tooltip: "Bold (**text**)",
      onAction: () => editor.execCommand("Bold"),
      onSetup: (api) => {
        api.setActive(editor.formatter.match("bold"));
        const changed = editor.formatter.formatChanged("bold", (state) => api.setActive(state));
        return () => changed.unbind();
      },
    });

    editor.ui.registry.addToggleButton("italic", {
      icon: "italic",
      tooltip: "Italic (*text*)",
      onAction: () => editor.execCommand("Italic"),
      onSetup: (api) => {
        api.setActive(editor.formatter.match("italic"));
        const changed = editor.formatter.formatChanged("italic", (state) => api.setActive(state));
        return () => changed.unbind();
      },
    });

    editor.ui.registry.addButton("codesample", {
      tooltip: "Insert code snippet",
      icon: "code-sample",
      onAction: openCodeBlockDialog,
      onSetup: (buttonApi) => {
        const onNodeChange = (e) => {
          const node = e.element;
          const isCodeBlock = editor.dom.getParent(node, "pre") !== null;
          buttonApi.setActive(isCodeBlock);
        };

        editor.on("NodeChange", onNodeChange);

        return () => {
          editor.off("NodeChange", onNodeChange);
        };
      },
    });

    editor.ui.registry.addButton("table", {
      tooltip: "Insert table",
      icon: "table",
      onAction: openTableDialog,
    });

    editor.ui.registry.addToggleButton("bullist", {
      icon: "unordered-list",
      tooltip: "Bullet list (* text ↵)",
      onAction: () => editor.execCommand("InsertUnorderedList"),
      onSetup: function (api) {
        const editorEventCallback = function () {
          api.setActive(editor.dom.getParent(editor.selection.getNode(), "ul") !== null);
        };
        editor.on("NodeChange", editorEventCallback);
        return () => editor.off("NodeChange", editorEventCallback);
      },
    });

    editor.ui.registry.addToggleButton("numlist", {
      icon: "ordered-list",
      tooltip: "Numbered list (1. text ↵)",
      onAction: () => editor.execCommand("InsertOrderedList"),
      onSetup: function (api) {
        const editorEventCallback = function () {
          api.setActive(editor.dom.getParent(editor.selection.getNode(), "ol") !== null);
        };
        editor.on("NodeChange", editorEventCallback);
        return () => editor.off("NodeChange", editorEventCallback);
      },
    });

    editor.ui.registry.addToggleButton("blockquote", {
      icon: "quote",
      tooltip: "Quote (> text ↵)",
      onAction: () => editor.execCommand("mceBlockQuote"),
      onSetup: (api) => {
        api.setActive(editor.formatter.match("blockquote"));
        const changed = editor.formatter.formatChanged("blockquote", (state) => api.setActive(state));
        return () => changed.unbind();
      },
    });

    editor.ui.registry.addToggleButton("note", {
      icon: "note",
      tooltip: "Note",
      onAction: () => editor.execCommand("a51ToggleCallout", "aside-note"),
      onSetup: (api) => {
        const handler = function () {
          api.setEnabled(!invalidCalloutSelection());
        };
        editor.on("NodeChange", handler);
        api.setActive(editor.formatter.match("note"));
        const changed = editor.formatter.formatChanged("note", (state) => api.setActive(state));

        return () => {
          changed.unbind();
          editor.off("NodeChange", handler);
        };
      },
    });

    editor.ui.registry.addButton("indent", {
      tooltip: "Increase indent (⇥)",
      icon: "indent",
      onAction: () => {
        editor.execCommand("Indent");
      },
    });

    editor.ui.registry.addButton("outdent", {
      tooltip: "Decrease indent (⇧ + ⇥)",
      icon: "outdent",
      onAction: () => {
        editor.execCommand("Outdent");
      },
    });

    editor.ui.registry.addToggleButton("link", {
      tooltip: "Insert link",
      icon: "link",
      onAction: openLinkDialog,
      onSetup: (buttonApi) => {
        const onNodeChange = (e) => {
          const node = e.element;
          const isLink = editor.dom.getParent(node, "a[href]") !== null;
          buttonApi.setActive(isLink);
        };

        editor.on("NodeChange", onNodeChange);

        return () => {
          editor.off("NodeChange", onNodeChange);
        };
      },
    });
    editor.ui.registry.addButton("confirmTableDelete", {
      icon: "table-delete-table",
      tooltip: "Delete table",
      onAction: openDeleteTableDialog,
    });
    editor.ui.registry.addMenuButton("formatingOptionsMenu", {
      icon: "menu",
      tooltip: "Formatting options",
      fetch: (callback) => {
        callback([
          {
            type: "menuitem",
            text: "Underline",
            icon: "underline",
            onAction: () => editor.execCommand("Underline"),
            shortcut: "_text_",
          },
          {
            type: "menuitem",
            text: "Strikethrough",
            icon: "strike-through",
            onAction: () => editor.execCommand("Strikethrough"),
          },
          {
            type: "menuitem",
            text: "Monospace",
            icon: "inline-code",
            onAction: () => {
              editor.formatter.toggle("inline-code");
            },
            shortcut: "`text`",
          },
          { type: "separator" },
          {
            type: "menuitem",
            text: "Title",
            icon: "title",
            onAction: () => {
              editor.formatter.toggle("h1");
            },
            shortcut: "# text ↵",
          },
          {
            type: "menuitem",
            text: "Subtitle",
            icon: "subtitle",
            onAction: () => {
              editor.formatter.toggle("h2");
            },
            shortcut: "## text ↵",
          },
          { type: "separator" },
          {
            type: "menuitem",
            text: "Clear styles",
            icon: "remove-formatting",
            onAction: () => editor.execCommand("RemoveFormat"),
          },
        ]);
      },
    });

    editor.ui.registry.addMenuButton("insertOptionsMenu", {
      icon: "menu",
      tooltip: "Insert...",
      fetch: (callback) => {
        callback([
          {
            type: "menuitem",
            text: "Table",
            icon: "table",
            onAction: () => openTableDialog(),
          },
          {
            type: "menuitem",
            text: "Horizontal line",
            icon: "horizontal-rule",
            onAction: () => editor.execCommand("mceInsertContent", false, "<hr />"),
            shortcut: "---",
          },
          ...(showTextVariables
            ? [
                {
                  type: "menuitem",
                  text: "Insert variable",
                  icon: "variable",
                  onAction: () => openVariableDialog(),
                },
              ]
            : []),
        ]);
      },
    });

    calloutButtonRegistry(editor);
    editor.ui.registry.addMenuButton("outlineOptionsMenu", {
      icon: "menu",
      tooltip: "More options...",
      fetch: (callback) => {
        const code = {
          type: "menuitem",
          text: "Code",
          icon: "code-sample",
          onAction: openCodeBlockDialog,
        };

        let options = calloutMenuOptions(editor);
        callback([code, ...options]);
      },
    });

    editor.formatter.register({
      "inline-code": {
        inline: "code",
      },
      note: {
        block: "aside",
        classes: "aside-note",
      },
    });

    editor.on("keydown", (e) => {
      if (e.key === "Tab") {
        const node = editor.selection.getNode();
        const isInList = editor.dom.getParent(node, "li");
        const isInTable = editor.dom.getParent(node, "td,th");

        if (isInList || isInTable) {
          return;
        }

        e.preventDefault();

        if (e.shiftKey) {
          editor.execCommand("Outdent");
        } else {
          editor.execCommand("Indent");
        }
      } else if (e.key.toLowerCase() === "k" && (e.ctrlKey || e.metaKey)) {
        openLinkDialog();
      }
    });

    editor.on("Paste", () => {
      const modalElement = document.getElementsByClassName("c-sheet__wrapper__paper")[0];
      const currentModalScrollTop = modalElement ? modalElement.scrollTop : 0;

      requestAnimationFrame(() => {
        if (modalElement) {
          modalElement.scrollTop = currentModalScrollTop;
        }
      });
    });

    editor.on("ExecCommand", (e) => {
      if (e.command === "mceTableInsertColBefore" || e.command === "mceTableInsertColAfter") {
        const table = editor.dom.getParent(editor.selection.getStart(), "table");
        if (table) {
          const cols = table.rows[0].cells.length;
          const newWidth = 100 / cols + "%";

          Array.from(table.rows).forEach((row) => {
            Array.from(row.cells).forEach((cell) => {
              editor.dom.setStyle(cell, "width", newWidth);
            });
          });
        }
      }
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
    }
  };

  return (
    <div
      className="visual_editor_tinymce_wrapper"
      data-testid="tiny-mce-editor"
    >
      <div
        id={tinymceToolbarId}
        data-qa-id="text-editor-toolbar"
        className="visual_editor_tinymce_toolbar"
      />
      <Editor
        onInit={handleInit}
        id={tinymceId}
        value={content}
        init={config}
        onEditorChange={handleEditorChange}
        onKeyDown={onKeyDown}
      />
      {linkDialogOpen && (
        <LinkDialog
          open={linkDialogOpen}
          onClose={closeLinkDialog}
          onSubmit={handleInsertLink}
          url={linkData.url}
          text={linkData.text}
          onKeyDown={handleKeyDown}
        />
      )}
      {tableDialogOpen && (
        <TableDialog
          open={tableDialogOpen}
          onClose={closeTableDialog}
          onSubmit={handleInsertTable}
          onKeyDown={handleKeyDown}
        />
      )}
      {codeBlockDialogOpen && (
        <CodeBlockDialog
          open={codeBlockDialogOpen}
          onClose={() => setCodeBlockDialogOpen(false)}
          onSubmit={handleCodeBlockInsert}
          selectedLanguage={codeBlockData.language}
          code={codeBlockData.code}
          onKeyDown={handleKeyDown}
        />
      )}
      {deleteTableDialog && (
        <ConfirmDialog
          open={deleteTableDialog}
          dialogTitle={t("Delete Table")}
          dialogContent={t("Are you sure you want to delete this table?")}
          confirmBtnText={t("Delete")}
          cancelBtnText={t("Cancel")}
          onConfirm={handleDeleteTable}
          onCancel={closeDeleteTableDialog}
        />
      )}
      {variableDialogOpen && (
        <VariableDialogWrapper
          open={variableDialogOpen}
          onClose={closeVariableDialog}
          onSubmit={handleInsertVariable}
          onKeyDown={handleKeyDown}
        />
      )}
    </div>
  );
};

VisualEditor.propTypes = {
  content: PropTypes.string,
  bodyMode: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  directionality: PropTypes.string,
  onFileUploadFailed: PropTypes.func,
  onKeyDown: PropTypes.func,
  onChange: PropTypes.func,
  autocompleteConfig: PropTypes.object,
  showTextVariables: PropTypes.bool,
};

VisualEditor.defaultProps = {
  content: ``,
  bodyMode: `paragraph`,
  placeholder: ``,
  directionality: `ltr`,
  autoFocus: false,
  onFileUploadFailed: () => null,
  onKeyDown: () => null,
  onChange: () => null,
  autocompleteConfig: {},
  showTextVariables: false,
};

export default VisualEditor;
